import { useEffect, useState } from 'react';
import { Activity, AlertTriangle, KeyRound, Mail, Zap, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';

interface MetricRow {
  day: string;
  active_users: number;
  drill_attempts: number;
  avg_drill_score: number;
  ai_calls: number;
  error_count: number;
  emails_sent: number;
}

interface ErrorRow {
  id: number;
  event_type: string;
  error_message: string;
  metadata: Record<string, unknown>;
  created_at: string;
}

export default function AdminHealthPage() {
  const { profile, loading } = useAuth();
  const [metrics, setMetrics] = useState<MetricRow[]>([]);
  const [errors, setErrors] = useState<ErrorRow[]>([]);
  const [days] = useState(14);
  const [fetching, setFetching] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [keyCheck, setKeyCheck] = useState<null | {
    ok: boolean;
    stage?: string;
    status?: number;
    message?: string;
    model_count?: number;
    mask?: { prefix: string; suffix: string; length: number };
    raw_length?: number;
  }>(null);
  const [keyChecking, setKeyChecking] = useState(false);

  const runKeyCheck = async () => {
    setKeyChecking(true);
    setKeyCheck(null);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/openai-key-check`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          'Content-Type': 'application/json',
        },
      });
      const json = await res.json();
      setKeyCheck(json);
    } catch (err) {
      setKeyCheck({ ok: false, stage: 'network', message: String(err) });
    } finally {
      setKeyChecking(false);
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!profile?.is_admin) {
      setAuthError('Admin access required');
      setFetching(false);
      return;
    }
    (async () => {
      const [{ data: m, error: mErr }, { data: e }] = await Promise.all([
        supabase.rpc('admin_health_metrics', { p_days: days }),
        supabase.from('system_logs').select('*').order('created_at', { ascending: false }).limit(20),
      ]);
      if (mErr) setAuthError(mErr.message);
      setMetrics((m as MetricRow[]) || []);
      setErrors((e as ErrorRow[]) || []);
      setFetching(false);
    })();
  }, [profile, loading, days]);

  if (loading || fetching) {
    return <div className="container-app py-12 text-sm text-ink-500">Loading admin metrics...</div>;
  }

  if (authError) {
    return (
      <div className="container-app py-20 max-w-md text-center">
        <AlertTriangle className="w-10 h-10 text-rose-500 mx-auto mb-4" />
        <h1 className="font-display text-3xl mb-2">Admin only</h1>
        <p className="text-ink-600 text-sm">{authError}</p>
      </div>
    );
  }

  const today = metrics[0];
  const yesterday = metrics[1];
  const weekTotals = metrics.slice(0, 7).reduce(
    (acc, r) => ({
      active: acc.active + Number(r.active_users || 0),
      ai: acc.ai + Number(r.ai_calls || 0),
      emails: acc.emails + Number(r.emails_sent || 0),
      errors: acc.errors + Number(r.error_count || 0),
    }),
    { active: 0, ai: 0, emails: 0, errors: 0 },
  );

  const maxActive = Math.max(1, ...metrics.map((m) => Number(m.active_users || 0)));
  const maxAi = Math.max(1, ...metrics.map((m) => Number(m.ai_calls || 0)));

  return (
    <div className="container-app py-12 max-w-6xl">
      <div className="mb-10">
        <div className="section-eyebrow mb-3">Admin · Health</div>
        <h1 className="font-display text-5xl">Last {days} days.</h1>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
        <StatCard icon={<Users className="w-4 h-4" />} label="Active today" value={Number(today?.active_users || 0)} delta={Number(today?.active_users || 0) - Number(yesterday?.active_users || 0)} />
        <StatCard icon={<Zap className="w-4 h-4" />} label="AI calls (7d)" value={weekTotals.ai} />
        <StatCard icon={<Mail className="w-4 h-4" />} label="Emails sent (7d)" value={weekTotals.emails} />
      </div>

      <div className="card p-6 mb-8">
        <div className="flex items-start justify-between mb-5 gap-4">
          <div>
            <div className="font-display text-2xl">OpenAI key check</div>
            <div className="text-sm text-ink-500">
              Pings OpenAI directly with the sanitized key the edge function actually sees.
            </div>
          </div>
          <button
            onClick={runKeyCheck}
            disabled={keyChecking}
            className="btn-primary inline-flex items-center gap-2 text-sm"
          >
            <KeyRound className="w-4 h-4" />
            {keyChecking ? 'Testing...' : 'Test key'}
          </button>
        </div>
        {keyCheck && (
          <div
            className={`p-4 rounded-lg border text-sm ${
              keyCheck.ok
                ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                : 'bg-rose-50 border-rose-200 text-rose-900'
            }`}
          >
            <div className="font-semibold mb-1">
              {keyCheck.ok ? `OK · OpenAI returned ${keyCheck.model_count} models` : `Failed at stage: ${keyCheck.stage}`}
            </div>
            {keyCheck.status !== undefined && <div>HTTP {keyCheck.status}</div>}
            {keyCheck.message && <div className="font-mono text-xs mt-1 break-all">{keyCheck.message}</div>}
            {keyCheck.mask && (
              <div className="font-mono text-xs mt-2 text-ink-600">
                key {keyCheck.mask.prefix}…{keyCheck.mask.suffix} (length {keyCheck.mask.length}, raw secret length {keyCheck.raw_length})
              </div>
            )}
          </div>
        )}
      </div>

      <div className="card p-6 mb-8">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="font-display text-2xl">Daily activity</div>
            <div className="text-sm text-ink-500">Active users vs. AI calls per day</div>
          </div>
          <Activity className="w-4 h-4 text-ink-400" />
        </div>
        <div className="space-y-2">
          {metrics.map((row) => {
            const activePct = (Number(row.active_users || 0) / maxActive) * 100;
            const aiPct = (Number(row.ai_calls || 0) / maxAi) * 100;
            return (
              <div key={row.day} className="grid grid-cols-[80px,1fr] items-center gap-3 text-xs">
                <div className="text-ink-500 font-mono">
                  {new Date(row.day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <div className="w-12 text-ink-500">Active</div>
                    <div className="flex-1 h-2 rounded-full bg-warm-100 overflow-hidden">
                      <div className="h-full bg-ink-700" style={{ width: `${activePct}%` }} />
                    </div>
                    <div className="w-10 text-right tabular-nums">{Number(row.active_users || 0)}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-12 text-ink-500">AI</div>
                    <div className="flex-1 h-2 rounded-full bg-warm-100 overflow-hidden">
                      <div className="h-full bg-accent-500" style={{ width: `${aiPct}%` }} />
                    </div>
                    <div className="w-10 text-right tabular-nums">{Number(row.ai_calls || 0)}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="card p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="font-display text-2xl">Recent errors</div>
            <div className="text-sm text-ink-500">{weekTotals.errors} logged in the last 7 days</div>
          </div>
          <AlertTriangle className="w-4 h-4 text-amber-500" />
        </div>
        {errors.length === 0 ? (
          <div className="text-sm text-ink-500 py-8 text-center">No errors logged. Nice.</div>
        ) : (
          <div className="space-y-2">
            {errors.map((e) => (
              <div key={e.id} className="p-3 rounded-lg bg-warm-50 border border-ink-100">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-xs font-semibold text-rose-700">{e.event_type}</div>
                  <div className="text-[10px] text-ink-400 tabular-nums">{new Date(e.created_at).toLocaleString()}</div>
                </div>
                <div className="text-sm text-ink-700 truncate">{e.error_message || '(no message)'}</div>
                {e.metadata && Object.keys(e.metadata).length > 0 && (
                  <div className="text-[11px] text-ink-500 mt-1 font-mono truncate">{JSON.stringify(e.metadata)}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, delta }: { icon: React.ReactNode; label: string; value: number; delta?: number }) {
  return (
    <div className="card p-5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-ink-500 mb-3">
        {icon} {label}
      </div>
      <div className="font-display text-3xl tabular-nums">{value.toLocaleString()}</div>
      {delta !== undefined && delta !== 0 && (
        <div className={`text-xs mt-1 ${delta > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
          {delta > 0 ? '+' : ''}{delta} vs yesterday
        </div>
      )}
    </div>
  );
}
